import React, { Fragment, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Wrapper from "containers/backend/common/component/Wrapper";
import Radio from "containers/backend/common/component/Radio";
import { DatePickerComponent, FormWrapperInputComponent } from "containers/backend/common/component/Index";
import { CAMPAIGN, DATE_TIME_FORMAT, DAY_OF_WEEK } from "constants/Define";
import './PromotionDateLimit.less';
import DatePicker from "react-multi-date-picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel";
import InputIcon from "react-multi-date-picker/components/input_icon";
import { Button, Card, Checkbox, Icon, message } from "antd";
import moment from "moment";
import classNames from "classnames";

const PromotionDateLimit = ({ ...props }) => {
    const { bundle, validation, onChangeCheckinDateLimitValue, onChangeCheckinDateLimitType, onResetValidation } = props;
    const { DATE_LIMIT } = CAMPAIGN;
    const { t } = useTranslation();

    const DATE_TYPE = DATE_LIMIT.HAS_RESTRICTION.subOptions[0].data;
    const DATE_RANGE_TYPE = DATE_LIMIT.HAS_RESTRICTION.subOptions[1].data;
    const DAY_OF_WEEK_TYPE = DATE_LIMIT.HAS_RESTRICTION.subOptions[2].data;

    const hasDateRestriction = (checkinDateLimitType) => {
        return checkinDateLimitType && checkinDateLimitType !== DATE_LIMIT.NONE.data;
    }

    const [dateLimitType, setDateLimitType] = useState(
        hasDateRestriction(bundle.checkinDateLimitType) ? DATE_LIMIT.HAS_RESTRICTION.key : DATE_LIMIT.NONE.key
    );
    const [dateLimitSubType, setDateLimitSubType] = useState(
        hasDateRestriction(bundle.checkinDateLimitType) ? bundle.checkinDateLimitType : DATE_TYPE
    );
    const [dateValue, setDateValue] = useState([]);
    const [dateRangeValue, setDateRangeValue] = useState([]);
    const [selectedDateRange, setSelectedDateRange] = useState([]);
    const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]);

    const formatDate = useCallback((date) => {
        return date instanceof Date ? date.toISOString() : date;
    }, []);

    const toMomentDate = useCallback((date) => {
        if (!date) return null;
        if (moment.isMoment(date)) return date.clone();
        if (date instanceof Date || typeof date === 'string') return moment(date);
        if (date.toDate && typeof date.toDate === 'function') return moment(date.toDate());
        if (date.year && date.month && date.day) {
            return moment({
                year: date.year,
                month: date.month.index,
                date: date.day,
            });
        }

        return moment(date);
    }, []);

    const isPastDate = useCallback((date) => {
        const parsedDate = toMomentDate(date);
        return parsedDate?.isValid() && parsedDate.isBefore(moment().startOf('day'), 'day');
    }, [toMomentDate]);

    const isOutsideCodeUseDate = useCallback((date) => {
        const parsedDate = toMomentDate(date);
        if (!parsedDate?.isValid()) return false;

        const startDate = toMomentDate(bundle.startDate);
        const endDate = toMomentDate(bundle.endDate);

        return (startDate?.isValid() && parsedDate.isBefore(startDate, 'day'))
            || (endDate?.isValid() && parsedDate.isAfter(endDate, 'day'));
    }, [bundle.startDate, bundle.endDate, toMomentDate]);

    const convertToDateObjects = useCallback((isoStrings) => {
        if (!Array.isArray(isoStrings)) return [];
        return isoStrings.map(isoString => new Date(isoString));
    }, []);

    const disabledDateByCodeUseRange = useCallback((current) => {
        return current && isOutsideCodeUseDate(current);
    }, [isOutsideCodeUseDate]);

    const resetValidation = useCallback(() => {
        if (onResetValidation) {
            onResetValidation();
        }
    }, [onResetValidation]);

    const resetData = useCallback(() => {
        updateCheckinDateLimitValue({
            dateRanges: null,
            dates: null,
            dayOfWeek: null
        });
    }, []);

    const updateCheckinDateLimitValue = useCallback((updates) => {
        if (onChangeCheckinDateLimitValue) {
            const checkinDateLimitValue = {
                ...bundle.checkinDateLimitValue,
                ...updates
            };
            onChangeCheckinDateLimitValue(checkinDateLimitValue);
        }
    }, [onChangeCheckinDateLimitValue, bundle.checkinDateLimitValue]);

    const updateDayOfWeek = useCallback((updates) => {
        if (onChangeCheckinDateLimitValue && dateLimitSubType === DAY_OF_WEEK_TYPE) {
            const checkinDateLimitValue = {
                ...bundle.checkinDateLimitValue,
                dayOfWeek: {
                    ...bundle.checkinDateLimitValue?.dayOfWeek,
                    ...updates
                }
            };
            onChangeCheckinDateLimitValue(checkinDateLimitValue);
        }
    }, [onChangeCheckinDateLimitValue, bundle.checkinDateLimitValue, dateLimitSubType, DAY_OF_WEEK_TYPE]);

    const handleDateRangeChange = useCallback((dates) => {
        const dateRange = dates?.fullValue || [];
        setSelectedDateRange(dateRange);

        if (dateLimitSubType === DAY_OF_WEEK_TYPE) {
            updateDayOfWeek({
                fromDate: dateRange[0] || null,
                toDate: dateRange[1] || null
            });
        }
    }, [updateDayOfWeek, dateLimitSubType, DAY_OF_WEEK_TYPE]);

    useEffect(() => {
        if (bundle.checkinDateLimitType == DATE_LIMIT.NONE.data) {
            setDateLimitType(DATE_LIMIT.NONE.key);
        } else {
            setDateLimitType(DATE_LIMIT.HAS_RESTRICTION.key)
            setDateLimitSubType(bundle.checkinDateLimitType)

            if (bundle?.checkinDateLimitValue?.dates && Array.isArray(bundle.checkinDateLimitValue.dates)) {
                setDateValue(bundle.checkinDateLimitValue.dates);
            } else {
                setDateValue([]);
            }

            setDateRangeValue(bundle?.checkinDateLimitValue?.dateRanges || [])

            if (bundle?.checkinDateLimitValue?.dayOfWeek) {
                const { fromDate, toDate, daysOfWeek } = bundle.checkinDateLimitValue.dayOfWeek;

                if (fromDate && toDate) {
                    setSelectedDateRange([moment(fromDate), moment(toDate)]);
                }

                if (daysOfWeek && Array.isArray(daysOfWeek)) {
                    setSelectedDaysOfWeek(daysOfWeek);
                }
            } else {
                setSelectedDaysOfWeek([]);
            }
        }
    }, [bundle])

    useEffect(() => {
        if (dateLimitSubType !== DATE_TYPE || !bundle.startDate || !Array.isArray(dateValue) || dateValue.length <= 0) {
            return;
        }

        const validDates = dateValue.filter(date => !isOutsideCodeUseDate(date));
        if (validDates.length !== dateValue.length) {
            setDateValue(validDates);
            updateCheckinDateLimitValue({ dates: validDates });
        }
    }, [bundle.startDate, bundle.endDate, dateLimitSubType, dateValue, DATE_TYPE, isOutsideCodeUseDate, updateCheckinDateLimitValue]);

    useEffect(() => {
        if (dateLimitSubType !== DATE_RANGE_TYPE || !bundle.startDate || !Array.isArray(dateRangeValue) || dateRangeValue.length <= 0) {
            return;
        }

        const validDateRanges = dateRangeValue.filter(range => {
            return !isOutsideCodeUseDate(range.fromDate) && !isOutsideCodeUseDate(range.toDate);
        });

        if (validDateRanges.length !== dateRangeValue.length) {
            setDateRangeValue(validDateRanges);
            updateCheckinDateLimitValue({ dateRanges: validDateRanges });
        }
    }, [bundle.startDate, bundle.endDate, dateLimitSubType, dateRangeValue, DATE_RANGE_TYPE, isOutsideCodeUseDate, updateCheckinDateLimitValue]);

    const handleDateLimitTypeChange = (e) => {
        const newType = e.target.value;
        setDateLimitType(newType);

        if (newType === DATE_LIMIT.NONE.key) {
            setDateLimitSubType(DATE_LIMIT.NONE.value);
            if (onChangeCheckinDateLimitType) {
                onChangeCheckinDateLimitType('NONE');
            }
            resetData();
        } else {
            setDateLimitSubType(DATE_TYPE);
            if (onChangeCheckinDateLimitType) {
                onChangeCheckinDateLimitType(DATE_TYPE);
            }
        }

        resetValidation();
    }

    const handleDateLimitSubTypeChange = (e) => {
        const newSubType = e.target.value;
        setDateLimitSubType(newSubType);
        setSelectedDateRange([]);
        setSelectedDaysOfWeek([]);

        if (onChangeCheckinDateLimitType) {
            onChangeCheckinDateLimitType(newSubType);
        }

        resetData();
        resetValidation();
    }

    const handleDateChange = (dates) => {
        if (Array.isArray(dates) && dates.some(isPastDate)) {
            message.warning({
                key: 'promotion-date-limit-past-date-warning',
                content: 'Ngày áp dụng đã qua vẫn được chọn. Vui lòng kiểm tra trước khi lưu.',
            });
        }

        if (Array.isArray(dates) && dates.some(isOutsideCodeUseDate)) {
            message.warning({
                key: 'promotion-date-limit-code-start-warning',
                content: 'Ngày áp dụng phải nằm trong khoảng Ngày bắt đầu dùng code.',
            });
        }

        const validDates = Array.isArray(dates) ? dates.filter(date => !isOutsideCodeUseDate(date)) : [];
        const parsedDates = validDates.map(formatDate);
        setDateValue(parsedDates);
        updateCheckinDateLimitValue({ dates: parsedDates });
    }

    const disabledOddDateByCodeStart = useCallback(({ date }) => {
        if (!isOutsideCodeUseDate(date)) return {};

        return {
            disabled: true,
            style: {
                color: '#bfbfbf',
                textDecoration: 'line-through',
            },
        };
    }, [isOutsideCodeUseDate]);

    const handleAddStage = () => {
        if (selectedDateRange && selectedDateRange.length === 2) {
            const newDateRange = {
                fromDate: selectedDateRange[0],
                toDate: selectedDateRange[1]
            };

            if (isOutsideCodeUseDate(newDateRange.fromDate) || isOutsideCodeUseDate(newDateRange.toDate)) {
                message.warning({
                    key: 'promotion-date-limit-date-range-warning',
                    content: 'Khoảng thời gian áp dụng phải nằm trong khoảng Ngày bắt đầu dùng code.',
                });
                return;
            }

            const isDuplicate = dateRangeValue.some(existingRange => {
                const existingFromDate = moment(existingRange.fromDate).format(DATE_TIME_FORMAT.SHORT_DATE_EXPORT);
                const existingToDate = moment(existingRange.toDate).format(DATE_TIME_FORMAT.SHORT_DATE_EXPORT);
                const newFromDate = moment(newDateRange.fromDate).format(DATE_TIME_FORMAT.SHORT_DATE_EXPORT);
                const newToDate = moment(newDateRange.toDate).format(DATE_TIME_FORMAT.SHORT_DATE_EXPORT);

                return existingFromDate === newFromDate && existingToDate === newToDate;
            });

            if (!isDuplicate) {
                const newDateRangeValue = [...dateRangeValue, newDateRange];
                setDateRangeValue(newDateRangeValue);
                updateCheckinDateLimitValue({ dateRanges: newDateRangeValue });
            }

            setSelectedDateRange([]);
        }
    }

    const handleRemoveDateRange = (index) => {
        const newDateRangeValue = dateRangeValue.filter((_, i) => i !== index);
        setDateRangeValue(newDateRangeValue);
        updateCheckinDateLimitValue({ dateRanges: newDateRangeValue });
    }

    const handleDeleteAll = () => {
        setDateRangeValue([]);
        updateCheckinDateLimitValue({ dateRanges: [] });
    }

    const handleDaysOfWeekChange = (checkedValues) => {
        setSelectedDaysOfWeek(checkedValues);

        if (dateLimitSubType === DAY_OF_WEEK_TYPE) {
            updateDayOfWeek({
                fromDate: selectedDateRange[0] || bundle.checkinDateLimitValue?.dayOfWeek?.fromDate,
                toDate: selectedDateRange[1] || bundle.checkinDateLimitValue?.dayOfWeek?.toDate,
                daysOfWeek: checkedValues
            });
        }
    }

    const CustomInputDatePicker = ({ onFocus, value, onChange }) => {
        return (
            <div className="custom-date-picker-wrapper">
                <textarea
                    rows="3"
                    cols="40"
                    onFocus={onFocus}
                    value={value}
                    onChange={onChange}
                    className={classNames({
                        "border-red": !!validation.checkinDateLimitValue
                    })}
                />
                <Icon type="calendar" className="calendar-icon" />
            </div>
        )
    }

    return (
        <Fragment>
            <Wrapper className="col-md-6 promotion-date-restriction">
                <FormWrapperInputComponent
                    labelTitle={t(`item.bundle.promotionCodeDateRestriction`)}
                >
                    <Wrapper className="col-md-12 m-0 p-0">
                        <Radio.Group
                            value={dateLimitType}
                            onChange={handleDateLimitTypeChange}
                        >
                            <Radio className="mr-4" key={DATE_LIMIT.HAS_RESTRICTION.id} value={DATE_LIMIT.HAS_RESTRICTION.key}>
                                {t(DATE_LIMIT.HAS_RESTRICTION.label)}
                            </Radio>
                            <Radio className="mr-4" key={DATE_LIMIT.NONE.id} value={DATE_LIMIT.NONE.key}>
                                {t(DATE_LIMIT.NONE.label)}
                            </Radio>
                        </Radio.Group>

                        {dateLimitType === DATE_LIMIT.HAS_RESTRICTION.key && (
                            <Wrapper className="date-restriction">
                                <Radio.Group
                                    value={dateLimitSubType}
                                    onChange={handleDateLimitSubTypeChange}
                                >
                                    {DATE_LIMIT.HAS_RESTRICTION.subOptions.map(item => (
                                        <Radio className="mr-5" key={item.value} value={item.data}>
                                            {t(item.label)}
                                        </Radio>
                                    ))}
                                </Radio.Group>

                                {dateLimitSubType === DATE_TYPE && (
                                    <Wrapper className="odd-date">
                                        <FormWrapperInputComponent
                                            labelTitle={[t('item.bundle.choose'), t('item.bundle.applyDates')]}
                                            isRequired={'right'}
                                            isValidation={true}
                                            validation={validation.checkinDateLimitValue}
                                            messageBelowInput={true}
                                        >
                                            <Wrapper>
                                                <DatePicker
                                                    value={convertToDateObjects(dateValue) || [new Date()]}
                                                    onChange={handleDateChange}
                                                    format={DATE_TIME_FORMAT.SHORT_DATE}
                                                    mapDays={disabledOddDateByCodeStart}
                                                    sort
                                                    multiple
                                                    plugins={[
                                                        <DatePanel header="Ngày đã chọn" />
                                                    ]}
                                                    render={<CustomInputDatePicker />}
                                                    calendarPosition={`top-start`}
                                                    fixMainPosition={true}
                                                    fixRelativePosition={true}
                                                />
                                            </Wrapper>
                                        </FormWrapperInputComponent>
                                    </Wrapper>
                                )}

                                {dateLimitSubType === DATE_RANGE_TYPE && (
                                    <Wrapper className="date-range">
                                        <FormWrapperInputComponent
                                            labelTitle={[t('item.bundle.choose'), t('item.bundle.applyDateRanges')]}
                                            isRequired={'right'}
                                            isValidation={true}
                                            validation={validation.checkinDateLimitValueDateRange}
                                            messageBelowInput={true}
                                            className="mb-0"
                                        >
                                            <Wrapper className="date-range-content">
                                                <Wrapper className="dateRanges">
                                                    <DatePickerComponent
                                                        hmsComponent={`RangePicker`}
                                                        format={DATE_TIME_FORMAT.SHORT_DATE}
                                                        name={`dateRanges`}
                                                        placeholder={[t('placeHolderForm.startDate'), t('placeHolderForm.endDate')]}
                                                        disabledDate={disabledDateByCodeUseRange}
                                                        onChange={handleDateRangeChange}
                                                        value={selectedDateRange}
                                                        className={classNames({
                                                            "border-red": !!validation.checkinDateLimitValueDateRange
                                                        })}
                                                    />
                                                    {dateRangeValue.length > 0 && (
                                                        dateRangeValue.map((date, index) => (
                                                            <Card key={index} className="range-item">
                                                                <div>
                                                                    <span>{moment(date.fromDate).format(DATE_TIME_FORMAT.SHORT_DATE)}</span>
                                                                    ~
                                                                    <span>{moment(date.toDate).format(DATE_TIME_FORMAT.SHORT_DATE)}</span>
                                                                </div>
                                                                <span className="delete" onClick={() => handleRemoveDateRange(index)}>
                                                                    <Icon type="close-circle" />
                                                                </span>
                                                            </Card>
                                                        ))
                                                    )}
                                                </Wrapper>
                                                <Wrapper className="action">
                                                    <Button onClick={handleAddStage}>
                                                        {t('item.bundle.addStage')}
                                                    </Button>
                                                    <Button onClick={handleDeleteAll}>
                                                        {t('item.bundle.deleteAll')}
                                                    </Button>
                                                </Wrapper>
                                            </Wrapper>
                                        </FormWrapperInputComponent>
                                    </Wrapper>
                                )}

                                {dateLimitSubType === DAY_OF_WEEK_TYPE && (
                                    <Wrapper className="date-of-week">
                                        <FormWrapperInputComponent
                                            labelTitle={[t('item.bundle.choose'), t('item.bundle.applyDateRanges')]}
                                            isRequired={'right'}
                                            isValidation={true}
                                            validation={validation.checkinDateLimitValueDateRange}
                                            messageBelowInput={true}
                                            className="mb-0"
                                        >
                                            <Wrapper>
                                                <DatePickerComponent
                                                    hmsComponent={`RangePicker`}
                                                    format={DATE_TIME_FORMAT.SHORT_DATE}
                                                    name={`dateRanges`}
                                                    placeholder={[t('placeHolderForm.startDate'), t('placeHolderForm.endDate')]}
                                                    disabledDate={disabledDateByCodeUseRange}
                                                    onChange={handleDateRangeChange}
                                                    value={selectedDateRange}
                                                    className={classNames({
                                                        "border-red": !!validation.checkinDateLimitValueDateRange
                                                    })}
                                                />
                                            </Wrapper>
                                        </FormWrapperInputComponent>
                                        <Wrapper className="week">
                                            <FormWrapperInputComponent
                                                labelTitle={[t('item.bundle.choose'), t('item.bundle.applyDaysOfWeek')]}
                                                isRequired={'right'}
                                                isValidation={true}
                                                validation={validation.checkinDateLimitValueDaysOfWeek}
                                                className="mb-0"
                                            >
                                                <Checkbox.Group
                                                    value={selectedDaysOfWeek}
                                                    onChange={handleDaysOfWeekChange}
                                                >
                                                    {DAY_OF_WEEK.map((item, index) => (
                                                        <Checkbox key={index} value={item.name}
                                                            className={classNames({
                                                                "border-checkbox-red": !!validation.checkinDateLimitValueDaysOfWeek
                                                            })}
                                                        >
                                                            {t(item.label)}
                                                        </Checkbox>
                                                    ))}
                                                </Checkbox.Group>
                                            </FormWrapperInputComponent>
                                        </Wrapper>
                                    </Wrapper>
                                )}
                            </Wrapper>
                        )}
                    </Wrapper>
                </FormWrapperInputComponent>
            </Wrapper>
        </Fragment>
    )
}

export default PromotionDateLimit;
